import { AUTH_CONSTANTS } from "@/constants";
import type { PartnerBrandingConfig } from "@/types/partner";

/**
 * Selects active branding colors from a partner-like record, treating an EMPTY dedicated
 * column as absent. `partners.branding` defaults to `{}` (coronis-apps migration 000031) and
 * `GET /partners/{id}` returns it verbatim, so a not-yet-backfilled partner yields
 * `branding: {}` — which is truthy. A plain `partner.branding ?? configuration.branding` would
 * keep `{}` and mask colors still living in configuration.branding (unbranded theme on
 * login/reload/preview, #4116 review). Gate on `brand_primary` so empty falls through.
 */
export const pickBranding = (
  p?: {
    branding?: PartnerBrandingConfig | null;
    configuration?: { branding?: PartnerBrandingConfig | null } | null;
  } | null
): PartnerBrandingConfig | undefined => {
  if (p?.branding?.brand_primary) return p.branding;
  const cfg = p?.configuration?.branding;
  return cfg?.brand_primary ? cfg : undefined;
};

/** Normalized inputs for the partner_branding sessionStorage cache. */
export interface BrandingCacheInput {
  // Color-only structural shape so both PartnerBrandingConfig and the theme's
  // PartnerBranding (and raw parsed colors) are accepted.
  branding?: {
    brand_primary?: string;
    brand_secondary?: string;
    brand_primary_dark?: string;
    brand_secondary_dark?: string;
  } | null;
  logo_image?: string | null;
  logo_image_dark?: string | null;
  partner_company_name?: string | null;
}

/**
 * Single source of truth for the SHAPE of the `partner_branding` sessionStorage cache.
 * Every site that pre-warms or syncs the cache (login, partner switch, useAppTheme)
 * routes through this so the implementations can't diverge — previously there were four
 * separate inline writers, including PartnerSwitcher's bespoke double-nesting parse (#4116).
 *
 * Overwrites wholesale. Returns false (and writes nothing) when no primary color is present.
 */
export const writeBrandingCache = (input: BrandingCacheInput): boolean => {
  const b = input.branding;
  if (!b?.brand_primary) return false;

  const cache: Record<string, string> = { brand_primary: b.brand_primary };
  if (b.brand_secondary) cache.brand_secondary = b.brand_secondary;
  if (b.brand_primary_dark) cache.brand_primary_dark = b.brand_primary_dark;
  if (b.brand_secondary_dark) cache.brand_secondary_dark = b.brand_secondary_dark;
  // Logos are top-level partner columns, not part of the branding colors object.
  if (input.logo_image) cache.logo_image = input.logo_image;
  if (input.logo_image_dark) cache.logo_image_dark = input.logo_image_dark;
  if (input.partner_company_name) cache.partner_company_name = input.partner_company_name;

  sessionStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_BRANDING, JSON.stringify(cache));
  return true;
};

/** Shape of GET /partners/{id}/branding. `branding` is the colors object (or a JSON string). */
export interface BrandingEndpointResponse {
  branding?: unknown;
  logoImage?: string;
  logoImageDark?: string;
  partnerName?: string;
}

/**
 * Pre-warms the cache from a GET /partners/{id}/branding response ahead of the partner-switch
 * reload. When the target partner has no colors, the cache is CLEARED (not left intact) so the
 * previous tenant's branding can't carry over (#4116). Returns whether colors were written.
 */
export const cacheSwitchedPartnerBranding = (
  resp: BrandingEndpointResponse | null | undefined,
  fallbackName?: string
): boolean => {
  const raw = resp?.branding;
  const colors = (typeof raw === "string" ? JSON.parse(raw) : raw) as BrandingCacheInput["branding"];
  const wrote = writeBrandingCache({
    branding: colors,
    logo_image: resp?.logoImage,
    logo_image_dark: resp?.logoImageDark,
    partner_company_name: resp?.partnerName || fallbackName,
  });
  if (!wrote) sessionStorage.removeItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_BRANDING);
  return wrote;
};

/**
 * Updates the sessionStorage branding cache from a partner record and dispatches
 * a "partner-branding-updated" event so App.tsx can recompute the active theme.
 *
 * Call this after any successful partner branding update (color save, dark mode
 * generation, logo upload) to make the theme reflect changes immediately without
 * a full page reload.
 */
export const syncBrandingCache = (partnerRecord: Record<string, unknown>) => {
  try {
    // Only sync if this is the currently active partner — editing a different
    // partner's branding (e.g. from the partner list) should not affect the theme.
    const activePartnerId = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.ACTIVE_PARTNER_ID);
    if (activePartnerId && partnerRecord?.id && String(partnerRecord.id) !== activePartnerId) return;

    // Canonical dedicated branding column, treating empty `{}` as absent (#4116).
    const branding = pickBranding(partnerRecord as Parameters<typeof pickBranding>[0]);

    // Single shared cache-write implementation; then notify App.tsx to recompute the theme.
    const wrote = writeBrandingCache({
      branding,
      logo_image: partnerRecord.logo_image as string | undefined,
      logo_image_dark: partnerRecord.logo_image_dark as string | undefined,
      partner_company_name: partnerRecord.partner_company_name as string | undefined,
    });
    if (wrote) window.dispatchEvent(new Event("partner-branding-updated"));
  } catch {
    // Non-fatal — theme just won't update until next reload
  }
};
